import currency from 'currency.js';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Message } from 'rsuite';
import AuthStorage from '@services/authStorage';
import _ from 'lodash';
import moment from 'moment';

function hasToken(): boolean {
  const storage = AuthStorage.getInstance();
  const token = storage.getAccessToken();

  return token != null;
}

function buildMessage(message: string): JSX.Element {
  return (
    <Message showIcon closable type="success">
      {message}
    </Message>
  );
}

function buildInfoMessage(message: string): JSX.Element {
  return (
    <Message showIcon closable type="info">
      {message}
    </Message>
  );
}

function buildErrorMessage(message: string): JSX.Element {
  return (
    <Message showIcon closable type="error">
      {message}
    </Message>
  );
}

function toLink(s: string): string {
  let website = s;
  if (_.toUpper(website) === 'N/A') {
    website = '';
  }

  if (!website.match(/^[a-zA-Z]+:\/\//) && !_.isEmpty(website)) {
    website = `https://${website}`;
  }

  return website;
}

function blobToBase64(blob: File | any): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arr = _.split(String(reader.result), ',');
      const result = _.last(arr);
      resolve(String(result));
    };
    reader.readAsDataURL(blob);
  });
}

function formatCurrency(value: number | undefined, symbol = 'Rp '): string {
  return currency(value ? Number(value) : 0, {
    symbol,
    decimal: ',',
    separator: '.',
    precision: 0,
  }).format();
}

function formatSalaryRange(
  min: number | undefined,
  max: number | undefined,
): string {
  return `${formatCurrency(min)} - ${formatCurrency(max, '')}`;
}

function formatTime(second: number): string {
  return `00:${String(second).padStart(2, '0')}`;
}

function withRouter(Component: any): (props: any) => JSX.Element {
  const location = useLocation;
  const params = useParams;
  const navigate = useNavigate;
  // eslint-disable-next-line func-names
  return function (props: any): JSX.Element {
    return (
      <Component
        {...props}
        location={location()}
        navigate={navigate()}
        params={params()}
      />
    );
  };
}

function mapSeekingStatus(s: string): string | null {
  switch (s) {
    case 'ACTIVELY_LOOKING':
      return 'Actively looking for opportunities';
    case 'HIRED_OPEN':
      return 'Hired, but search for opportunities';
    case 'NOT_OPEN':
      return 'Not open for opportunities';
    default:
      return null;
  }
}

function printGender(gender: string): string {
  return gender === 'M' ? 'Male' : 'Female';
}

function mapBgUrl(str: string): string {
  let img = 'others';
  const s = str.toLowerCase();

  if (s.includes('business')) {
    img = 'business';
  } else if (s.includes('communication')) {
    img = 'communication';
  } else if (s.includes('computer science')) {
    img = 'computer-science';
  } else if (s.includes('creative')) {
    img = 'creative-design';
  } else if (s.includes('education')) {
    img = 'education';
  } else if (s.includes('engineering')) {
    img = 'engineering';
  } else if (s.includes('finance')) {
    img = 'finance';
  } else if (s.includes('healthcare')) {
    img = 'healthcare';
  } else if (s.includes('hospitality')) {
    img = 'hospitality';
  } else if (s.includes('legal')) {
    img = 'legal';
  } else if (s.includes('literature')) {
    img = 'literature';
  } else if (s.includes('psycology')) {
    img = 'psycology';
  } else if (s.includes('science')) {
    img = 'science';
  } else if (s.includes('urban living')) {
    img = 'urban-living';
  }

  // eslint-disable-next-line max-len
  return `https://tgpublic-bucket.s3.ap-southeast-1.amazonaws.com/assets/${img}.png`;
}

function companyBg(str: string): JSX.Element {
  const imgUrl = mapBgUrl(str);
  return (
    <img
      // eslint-disable-next-line max-len
      src={imgUrl}
      alt="card-header"
      width="100%"
    />
  );
}

function setNested(obj: object | any, path: string, value: any): any {
  const segments = parsePath(path);
  if (segments.length === 0) return obj;

  let result = Array.isArray(obj) ? [...obj] : { ...obj };
  if (typeof obj === 'string') {
    result = obj;
  }

  let cur = result;

  for (let i = 0; i < segments.length; i++) {
    const key = segments[i];
    const isLast = i === segments.length - 1;

    if (isLast) {
      if (typeof value === 'object') {
        const keys = Object.keys(value);
        if (!_.isObject(cur[key])) {
          cur[key] = {};
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const k of keys) {
          _.set(cur[key], k, value[k]);
        }
      } else {
        cur[key] = value;
      }
    } else {
      const nextKey = segments[i + 1];
      const isNextIndex = typeof nextKey === 'number';

      const next = cur[key] ?? (isNextIndex ? [] : {});
      cur[key] = Array.isArray(next) ? [...next] : { ...next };
      cur = cur[key];
    }
  }

  return result;
}

function parsePath(path: any): Array<string> {
  // Convert `a.b[1].c` → ["a", "b", 1, "c"]
  const regex = /([^[.\]]+)|\[(\d+)\]/g;
  const segments = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const match of path.matchAll(regex)) {
    if (match[1]) segments.push(match[1]);
    if (match[2]) segments.push(Number(match[2]));
  }

  return segments;
}

function parseHtmlBullet(html: string | undefined | null): Array<string> {
  if (!html) {
    return [];
  }

  // Use DOMParser in browser (or jsdom in Node.js)
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Collect all <li> text
  const items: string[] = [];
  doc.querySelectorAll('li').forEach((li) => {
    // innerText gives clean text (respects <br/> as newline)
    // replace newlines with space to normalize
    const text = li.innerText.replace(/\s+/g, ' ').trim();
    if (text) {
      items.push(text);
    }
  });

  // Handle <p>
  doc.querySelectorAll('p').forEach((p) => {
    const text = p.innerText.replace(/\s+/g, ' ').trim();
    if (text) items.push(text);
  });

  return items;
}

function parseHtmlBulletAsString(html: string | undefined | null): string {
  if (!html) {
    return '';
  }

  // Use DOMParser in browser (or jsdom in Node.js)
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Collect all <li> text
  const items: string[] = [];
  doc.querySelectorAll('li').forEach((li) => {
    // innerText gives clean text (respects <br/> as newline)
    // replace newlines with space to normalize
    const text = li.innerText.replace(/\s+/g, ' ').trim();
    if (text) {
      items.push(text);
    }
  });

  // Handle <p>
  doc.querySelectorAll('p').forEach((p) => {
    const text = p.innerText.replace(/\s+/g, ' ').trim();
    if (text) items.push(text);
  });

  return items.join('\n');
}

function setPickerDisabledDate(
  data: any,
  date: any,
  type: 'startDate' | 'endDate',
): boolean {
  if (type === 'startDate') {
    return _.get(data, 'endDate')
      ? moment(date).isAfter(moment(_.get(data, 'endDate'), 'DD/MM/YYYY'))
      : moment(date).isAfter(moment.now());
  }

  return _.get(data, 'startDate')
    ? moment(date).isBefore(moment(_.get(data, 'startDate'), 'DD/MM/YYYY')) ||
        moment(date).isAfter(moment.now())
    : moment(date).isAfter(moment.now());
}

function limitWords(str: string | undefined, n = 20): string {
  if (typeof str === 'undefined') {
    return '';
  }

  const parts = String(str).split(/\s+/).slice(0, n);
  const combined = parts.join(' ');
  if (parts.length >= 20) {
    return `${combined}...`;
  }

  return combined;
}

function countWords(str: string | undefined): number {
  if (typeof str === 'undefined') {
    return 0;
  }

  return String(str).split(/\s+/).length;
}

export default {
  hasToken,
  blobToBase64,
  buildMessage,
  buildInfoMessage,
  buildErrorMessage,
  countWords,
  formatCurrency,
  formatSalaryRange,
  formatTime,
  limitWords,
  printGender,
  parseHtmlBullet,
  parseHtmlBulletAsString,
  setNested,
  setPickerDisabledDate,
  mapSeekingStatus,
  withRouter,
  toLink,
  companyBg,
  mapBgUrl,
};
